/**
 * Platform-agnostic *instant* notification service for browser-class runtimes
 * (plain web, installed PWA, and the WebView inside the Tauri shell).
 *
 * Scheduling (OS-level reminders that fire while the app is closed) is NOT
 * handled here — that requires a native plugin and lives in each app's
 * platform adapter (see `apps/web/src/lib/notifications`). This bridge only
 * covers the foreground "show it now" path plus permission handling, which is
 * identical across every browser-class target.
 *
 * Production note — why Service Worker first:
 *   `new Notification()` throws `TypeError: Illegal constructor` on Android
 *   Chrome (and is deprecated on other mobile browsers). The only portable way
 *   to surface a system notification from a web/PWA context is
 *   `ServiceWorkerRegistration.showNotification()`. We prefer it whenever a SW
 *   is active and fall back to the constructor on desktop browsers without one.
 */

export type NotificationPermissionState = "granted" | "denied" | "default" | "unsupported";

export interface ShowNotificationOptions {
  body?: string;
  /** Arbitrary payload echoed back on notification click (e.g. `{ todoId }`). */
  data?: Record<string, unknown>;
  /** App icon badge count (uses the Badging API when available). */
  badge?: number;
  silent?: boolean;
  /** Collapses notifications that share a tag (defaults to `data.todoId`). */
  tag?: string;
  /** Notification icon URL. */
  icon?: string;
}

interface BrowserNotificationCtor {
  permission: NotificationPermission;
  requestPermission: () => Promise<NotificationPermission>;
  new (title: string, options?: NotificationOptions): Notification;
}

interface NotificationWindow {
  Notification?: BrowserNotificationCtor;
  navigator?: Navigator & {
    serviceWorker?: ServiceWorkerContainer;
    setAppBadge?: (count?: number) => Promise<void>;
    clearAppBadge?: () => Promise<void>;
  };
}

const getWindow = (): NotificationWindow | undefined =>
  typeof globalThis === "undefined"
    ? undefined
    : (globalThis as { window?: NotificationWindow }).window;

const getNotificationCtor = (win: NotificationWindow | undefined): BrowserNotificationCtor | null =>
  win && typeof win.Notification === "function" ? win.Notification : null;

const resolveTag = (options: ShowNotificationOptions): string | undefined => {
  if (options.tag) {
    return options.tag;
  }
  const todoId = options.data?.todoId;
  return typeof todoId === "string" ? todoId : undefined;
};

export class NotificationBridge {
  private readonly isWeb: boolean;

  constructor(options: { isWeb: boolean }) {
    this.isWeb = options.isWeb;
  }

  /** Current permission state without prompting the user. */
  getPermission(): NotificationPermissionState {
    if (!this.isWeb) {
      return "granted";
    }
    const ctor = getNotificationCtor(getWindow());
    if (!ctor) {
      return "unsupported";
    }
    return ctor.permission;
  }

  /** Prompts the user (if needed) and resolves to whether we may notify. */
  async requestPermission(): Promise<boolean> {
    if (!this.isWeb) {
      return true;
    }

    const ctor = getNotificationCtor(getWindow());
    if (!ctor) {
      return false;
    }

    if (ctor.permission === "granted") {
      return true;
    }
    if (ctor.permission === "denied") {
      return false;
    }

    try {
      const permission = await ctor.requestPermission();
      return permission === "granted";
    } catch (error) {
      console.error("[NotificationBridge] Failed to request permission:", error);
      return false;
    }
  }

  /** Shows a notification immediately. No-op on non-web (native) targets. */
  async show(title: string, options: ShowNotificationOptions = {}): Promise<void> {
    if (!this.isWeb) {
      return;
    }

    if (!(await this.requestPermission())) {
      return;
    }

    const win = getWindow();
    const tag = resolveTag(options);
    const notificationOptions: NotificationOptions = {
      body: options.body,
      silent: options.silent,
      tag,
      data: options.data,
      icon: options.icon,
    };

    if (await this.showViaServiceWorker(win, title, notificationOptions)) {
      this.applyBadge(win, options.badge);
      return;
    }

    const ctor = getNotificationCtor(win);
    if (ctor) {
      try {
        new ctor(title, notificationOptions);
      } catch (error) {
        // Android Chrome (and some mobile browsers) forbid the constructor.
        console.warn("[NotificationBridge] Notification constructor failed:", error);
      }
    }
    this.applyBadge(win, options.badge);
  }

  /** Updates the app icon badge count via the Badging API when supported. */
  private applyBadge(win: NotificationWindow | undefined, badge: number | undefined): void {
    if (badge === undefined) {
      return;
    }
    void this.setBadge(win?.navigator, badge);
  }

  private async setBadge(nav: NotificationWindow["navigator"], badge: number): Promise<void> {
    if (!nav) {
      return;
    }
    // The Badging API may be absent at runtime (older browsers); the try/catch
    // turns the resulting TypeError into a no-op.
    try {
      await (badge > 0 ? nav.setAppBadge(badge) : nav.clearAppBadge());
    } catch {
      // Badging API failures (unsupported / denied) are non-fatal.
    }
  }

  /**
   * Tries to surface the notification through an active Service Worker. Returns
   * `true` if it succeeded. This is the only reliable path on mobile browsers.
   */
  private async showViaServiceWorker(
    win: NotificationWindow | undefined,
    title: string,
    options: NotificationOptions,
  ): Promise<boolean> {
    const container = win?.navigator?.serviceWorker;
    if (!container) {
      return false;
    }
    try {
      const registration = await container.getRegistration();
      if (!registration) {
        return false;
      }
      await registration.showNotification(title, options);
      return true;
    } catch (error) {
      console.warn("[NotificationBridge] Service Worker notification failed:", error);
      return false;
    }
  }
}
